import { SimpleChange, SimpleChanges } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

class PropertyChange implements SimpleChange {
  readonly firstChange: boolean;

  constructor(public readonly previousValue: any, public readonly currentValue: any) {
    this.firstChange = this.previousValue === undefined;
  }

  isFirstChange(): boolean {
    return this.firstChange;
  }
}

export class ObservablePropertyBag {
  isRestored = false;

  private readonly subjectMap: Record<string, BehaviorSubject<any>> = {};
  private readonly defaults = {};
  private readonly lastValues = {};
  private readonly changedSubject = new Subject<SimpleChanges>();
  private changes: string[] = [];
  private isUpdating = false;

  getValue$<T>(name: string): Observable<T> {
    return this.ensure<T>(name).asObservable();
  }

  setValue<T>(name: string, value: T): void {
    const subject = this.ensure(name);
    if (subject.value !== value) {
      subject.next(value);
      this.queueChange(name);
    }
  }

  setDefaultValue<T>(name: string, value: T): void {
    this.defaults[name] = value;

    const subject = this.ensure<T>(name);
    if (subject.value === undefined) {
      this.setValue(name, value);
    }
  }

  get snapshot(): Record<string, any> {
    const snapshot = {};
    // eslint-disable-next-line guard-for-in
    for (const name in this.subjectMap) {
      snapshot[name] = this.subjectMap[name].value;
    }

    return snapshot;
  }

  get changed$(): Observable<SimpleChanges> {
    return this.changedSubject.asObservable();
  }

  clear(): void {
    // eslint-disable-next-line guard-for-in
    for (const name in this.subjectMap) {
      this.setValue(name, this.defaults[name]);
    }
  }

  restore(snapshot: Record<string, any>): void {
    // eslint-disable-next-line guard-for-in
    for (const name in snapshot) {
      this.setValue(name, snapshot[name]);
    }
  }

  /**
   *  Surpresses update notifications until endUpdate() is called.
   */
  beginUpdate(): void {
    this.isUpdating = true;
  }

  /**
   * Emits all update notifications since beginUpdate was called;
   */
  endUpdate(): void {
    this.isUpdating = false;
    this.flushChanges();
  }

  private ensure<T>(name: string): BehaviorSubject<T> {
    if (!this.subjectMap[name]) {
      this.subjectMap[name] = new BehaviorSubject<T>(this.defaults[name]);
    }

    return this.subjectMap[name];
  }

  private queueChange(name: string): void {
    this.changes.push(name);
    if (!this.isUpdating) {
      this.flushChanges();
    }
  }

  private flushChanges(): void {
    const changes: SimpleChanges = {};
    this.changes.forEach((name) => {
      const currentValue = this.ensure(name).value;
      changes[name] = new PropertyChange(this.lastValues[name], currentValue);
      this.lastValues[name] = currentValue;
    });
    this.changes = [];
    this.changedSubject.next(changes);
  }
}
